import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import cn from 'clsx'

// Actions
import { actions as authActions } from 'modules/auth/actions'

// Components
import { Button, Link, IconLogoWithSymbol, IconTracklibLogoSymbol } from '@tracklib/kit'
import Credits from 'components/header/credits'
import MobileMenu from 'components/mobilemenu'
import NavLinks from 'components/header/navlinks'
import ProfileMenu from 'components/header/profilemenu'
import SocialIcons from 'components/socialicons'

// Hooks
import { useTracking } from 'modules/tracking/hooks'
import { useProfile } from 'modules/profile/hooks'

// Types
import { CmsPage, State } from 'types'

// Utils
import { safeLocalStorage as localStorage } from 'utils'

import styles from './header.css'

const Header = (): JSX.Element | null => {
  const dispatch = useDispatch()
  const isImpersonating = !!localStorage.getItem('isImpersonating')
  const profile = useProfile()
  const { pathname } = useLocation()
  const isAuthenticated = useSelector<State, boolean>(state => state.auth.isAuthenticated)
  const headerVisible = useSelector<State, boolean>(state => state.header.headerVisible)
  const [showSideMenu, toggleShowSideMenu] = useState(false)
  const trackEvent = useTracking()
  const logout = () => dispatch(authActions.logout())
  const isStateOfSampling = pathname.includes('state-sampling') || pathname.includes('state-of-sampling')
  const isPlansPage = pathname.endsWith('plans/')
  const showStickyHeader = headerVisible && !isAuthenticated

  const heroType = useSelector<State, Array<CmsPage>>(state => Object.values(state.entities.pages)).filter(
    obj => obj.title === 'frontpage (v2021)',
  )[0]?.content?.[0]?.type

  const compressed =
    isStateOfSampling ||
    isPlansPage ||
    pathname.startsWith('/howitworks/') ||
    pathname.startsWith('/howtouse/') ||
    pathname.startsWith('/music/') ||
    pathname.startsWith('/licensing/') ||
    pathname.startsWith('/contact/') ||
    pathname.startsWith('/about/') ||
    pathname.startsWith('/press/') ||
    pathname.startsWith('/privacy/') ||
    pathname.startsWith('/cookies/') ||
    pathname.startsWith('/testimonials') ||
    (pathname.startsWith('/blog') && !pathname.startsWith('/blog/category') && !pathname.startsWith('/blog/all')) ||
    (heroType === 'front_page_hero_block' && pathname === '/')

  const darkCompressed = pathname === '/' || pathname.startsWith('/campaign/')

  const simpleHeader =
    (pathname.startsWith('/checkout') && !pathname.startsWith('/checkout/success/')) ||
    (pathname.startsWith('/license/') && !pathname.startsWith('/license/success/')) ||
    pathname.startsWith('/pay')

  const noHeader =
    pathname.startsWith('/signup') ||
    pathname.startsWith('/subscribe') ||
    pathname.startsWith('/interestform') ||
    pathname.startsWith('/onboard')

  const Logo = () => {
    const { groups } = profile
    return (
      <Link
        to={isAuthenticated ? '/inspiration/' : '/'}
        className={cn(styles.logo, compressed && !showStickyHeader && styles.logoCompressed)}
        ariaLabel="Home"
      >
        <span>
          <IconLogoWithSymbol />
          {groups && groups.some(x => x === 'betauser' || x === 'beta') && <span className={styles.beta}>Beta</span>}
        </span>
      </Link>
    )
  }

  const Burger = () => {
    return (
      <button
        className={cn(styles.burger, compressed && !showStickyHeader && styles.burgerCompressed)}
        onClick={() => toggleShowSideMenu(true)}
        datatesting="burgermenu"
        aria-label="Menu button"
      >
        <span />
      </button>
    )
  }

  if (noHeader) {
    return null
  }

  return (
    <header
      id="header"
      className={cn(
        styles.header,
        isImpersonating && styles.isImpersonating,
        showStickyHeader && styles.headerVisible,
        compressed && !showStickyHeader && styles.compressed,
        darkCompressed && styles.compressed,
        styles.sticky,
        styles.preload,
      )}
      role="banner"
    >
      {simpleHeader ? (
        <div className={styles.inner2}>
          <Logo />
        </div>
      ) : (
        <div className={styles.inner2}>
          <div className={styles.navigationLeft}>
            <Logo />
            {isImpersonating && <h2 className={styles.impersonating}>IMPERSONATING</h2>}
            <Burger />
            <MobileMenu visible={showSideMenu} toggle={toggleShowSideMenu} side="left" width={272}>
              <div className={styles.menu}>
                <div>
                  <Link to="/" onClick={() => toggleShowSideMenu(false)} className={styles.mobileSymbol}>
                    <IconTracklibLogoSymbol className={styles.symbol} />
                  </Link>
                  <NavLinks
                    isAuthenticated={isAuthenticated}
                    compressed={compressed && !showStickyHeader}
                    onLinkClick={() => toggleShowSideMenu(false)}
                  />
                </div>
                {showSideMenu && <SocialIcons className={styles.socialIcons} />}
              </div>
            </MobileMenu>
          </div>

          <div className={styles.navigationRight}>
            {isAuthenticated ? (
              <>
                <Credits compressed={compressed} hideOnMobile />
                <ProfileMenu onLogout={() => logout()} />
              </>
            ) : (
              <>
                <div className={styles.desktopSignupLinks}>
                  <div className={styles.loginSignupLinks}>
                    <Button
                      to="login"
                      variant={['transparent', compressed && !showStickyHeader ? 'white' : '']}
                      modal
                      className={styles.loginButton}
                    >
                      Login
                    </Button>
                    <Button
                      to="/subscribe/"
                      variant={compressed && !showStickyHeader ? 'white' : ''}
                      onClick={() => trackEvent('Clicked "sign up now" in nav')}
                    >
                      Sign up now
                    </Button>
                  </div>
                </div>

                <div className={styles.mobileLoginLink}>
                  <Button to="login" size="small" variant={!darkCompressed ? 'white' : ''} modal>
                    Login
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  )
}

export default Header
